import { useEffect, useState, type FormEvent } from "react";
import { CheckCircle2, Loader2, Send, X } from "lucide-react";
import {
  submitManualSupportTicket,
  type ManualSupportTicketResult,
} from "../../lib/manual-support-ticket";

interface ManualSupportTicketDialogProps {
  open: boolean;
  version: string;
  onClose: () => void;
}

type SubmitPhase = "idle" | "submitting" | "success" | "error";

export function ManualSupportTicketDialog({
  open,
  version,
  onClose,
}: ManualSupportTicketDialogProps) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [phase, setPhase] = useState<SubmitPhase>("idle");
  const [message, setMessage] = useState<string | null>(null);
  const [result, setResult] = useState<ManualSupportTicketResult | null>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose, open]);

  if (!open) return null;

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const nextTitle = title.trim();
    const nextDescription = description.trim();
    if (nextTitle.length < 4 || nextDescription.length < 10) {
      setPhase("error");
      setMessage("Add a short title and a few details.");
      return;
    }

    setPhase("submitting");
    setMessage(null);
    setResult(null);
    try {
      const submitted = await submitManualSupportTicket({
        title: nextTitle,
        descriptionMarkdown: nextDescription,
        issueType: "support",
        severity: "medium",
        appVersion: version,
        surface: document.title || "Fintheon",
        url: window.location.href,
        userAgent: navigator.userAgent,
      });
      setResult(submitted);
      setPhase("success");
      setMessage("Ticket filed.");
    } catch (error) {
      setPhase("error");
      setMessage(
        error instanceof Error
          ? error.message
          : "Support ticket could not be filed.",
      );
    }
  }

  return (
    <div className="manual-support-ticket-dialog absolute bottom-7 left-0 z-[90] w-[min(430px,calc(100vw-1.5rem))] rounded-lg border border-[var(--fintheon-accent)]/20 p-3 text-left text-[var(--fintheon-text)]">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--fintheon-accent)]">
            Manual Ticket
          </p>
          <p className="mt-1 text-[11px] leading-snug text-[var(--fintheon-text)]/52">
            Files to Fintheon Linear.
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="text-[var(--fintheon-text)]/34 transition-colors hover:text-[var(--fintheon-text)]/72"
          title="Close"
        >
          <X size={13} />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="mt-3 grid gap-2">
        <label className="grid gap-1">
          <span className="text-[9px] uppercase tracking-[0.14em] text-[var(--fintheon-text)]/38">
            Title
          </span>
          <input
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            maxLength={180}
            className="h-9 rounded-md border border-[var(--fintheon-accent)]/14 bg-[var(--fintheon-bg)]/54 px-2 text-[12px] outline-none transition-colors focus:border-[var(--fintheon-accent)]/36"
            placeholder="Short issue title"
          />
        </label>
        <label className="grid gap-1">
          <span className="text-[9px] uppercase tracking-[0.14em] text-[var(--fintheon-text)]/38">
            Brief
          </span>
          <textarea
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            maxLength={4000}
            rows={5}
            className="resize-none rounded-md border border-[var(--fintheon-accent)]/14 bg-[var(--fintheon-bg)]/54 px-2 py-2 text-[12px] leading-relaxed outline-none transition-colors focus:border-[var(--fintheon-accent)]/36"
            placeholder="What happened, where, and what you expected."
          />
        </label>

        {message ? (
          <p
            className={`text-[10px] leading-snug ${
              phase === "error"
                ? "text-red-300/72"
                : "text-[var(--fintheon-text)]/58"
            }`}
            aria-live="polite"
          >
            {message}
            {result?.issue?.identifier ? ` ${result.issue.identifier}` : ""}
          </p>
        ) : null}

        <div className="flex items-center justify-end gap-2">
          {result?.issue?.url ? (
            <a
              href={result.issue.url}
              target="_blank"
              rel="noreferrer"
              className="text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--fintheon-accent)]/82 transition-colors hover:text-[var(--fintheon-accent)]"
            >
              Open Linear
            </a>
          ) : null}
          <button
            type="submit"
            disabled={phase === "submitting"}
            className="fintheon-glass-cta relative inline-flex h-8 items-center justify-center gap-2 overflow-hidden rounded-md border px-3 text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--fintheon-accent)] transition-colors disabled:cursor-not-allowed disabled:opacity-45"
          >
            {phase === "submitting" ? (
              <Loader2 size={13} className="animate-spin" />
            ) : phase === "success" ? (
              <CheckCircle2 size={13} />
            ) : (
              <Send size={13} />
            )}
            Submit
          </button>
        </div>
      </form>
    </div>
  );
}
